/**
 * SchemaForm — automatický formulář z JSON Schema.
 *
 * Podporované typy:
 *   string     → Input (+ format: url, email, password, textarea, cron)
 *   number     → Input[type=number] (min/max)
 *   boolean    → Toggle
 *   enum       → select dropdown
 *   array      → dynamic list (add/remove)
 *   object     → nested fieldset (collapsible)
 *
 * Použití:
 *   <SchemaForm schema={module.options} values={config} onChange={setConfig} />
 *   <SchemaForm schema={module.options} values={config} onSave={handleSave} readonly />
 */
import { type ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { Input } from './Input';
import { Label } from './Label';
import { Textarea } from './Textarea';
import { Toggle } from './Toggle';
import { Select } from './Select';
import { cn } from '../../lib/utils';
import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';

// ── Schema types ──────────────────────────────────────────────────────────────

export interface SchemaProperty {
  type: 'string' | 'number' | 'boolean' | 'array' | 'object';
  label?: string;
  description?: string;
  default?: unknown;
  required?: boolean;
  // string
  format?: 'url' | 'email' | 'password' | 'textarea' | 'cron' | 'code';
  enum?: string[];
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  placeholder?: string;
  // number
  minimum?: number;
  maximum?: number;
  step?: number;
  // array
  items?: SchemaProperty;
  minItems?: number;
  maxItems?: number;
  // object
  properties?: Record<string, SchemaProperty>;
}

export type OptionsSchema = Record<string, SchemaProperty>;

interface SchemaFormProps {
  schema: OptionsSchema;
  values: Record<string, unknown>;
  onChange?: (values: Record<string, unknown>) => void;
  onSave?: (values: Record<string, unknown>) => void | Promise<void>;
  readonly?: boolean;
  className?: string;
  /** Compact mode — menší mezery */
  compact?: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getDefault(prop: SchemaProperty): unknown {
  if (prop.default !== undefined) return prop.default;
  switch (prop.type) {
    case 'string':
      return '';
    case 'number':
      return prop.minimum ?? 0;
    case 'boolean':
      return false;
    case 'array':
      return [];
    case 'object': {
      const obj: Record<string, unknown> = {};
      if (prop.properties) {
        for (const [k, v] of Object.entries(prop.properties)) {
          obj[k] = getDefault(v);
        }
      }
      return obj;
    }
  }
}

function validate(value: unknown, prop: SchemaProperty): string | null {
  if (prop.required && (value === undefined || value === null || value === '')) {
    return i18n.t('ui.required', 'Povinné pole');
  }
  if (prop.type === 'string' && typeof value === 'string') {
    if (prop.minLength && value.length < prop.minLength) {
      return i18n.t('ui.minChars', 'Min {{count}} znaků', { count: prop.minLength });
    }
    if (prop.maxLength && value.length > prop.maxLength) {
      return i18n.t('ui.maxChars', 'Max {{count}} znaků', { count: prop.maxLength });
    }
    if (prop.pattern && !new RegExp(prop.pattern).test(value)) {
      return i18n.t('ui.invalidFormat', 'Neplatný formát');
    }
    if (prop.format === 'url' && value && !/^https?:\/\/.+/.test(value)) {
      return i18n.t('ui.invalidUrl', 'Neplatná URL');
    }
    if (prop.format === 'email' && value && !value.includes('@')) {
      return i18n.t('ui.invalidEmail', 'Neplatný email');
    }
  }
  if (prop.type === 'number' && typeof value === 'number') {
    if (prop.minimum !== undefined && value < prop.minimum) {
      return i18n.t('ui.minValue', 'Min {{value}}', { value: prop.minimum });
    }
    if (prop.maximum !== undefined && value > prop.maximum) {
      return i18n.t('ui.maxValue', 'Max {{value}}', { value: prop.maximum });
    }
  }
  if (prop.type === 'array' && Array.isArray(value)) {
    if (prop.minItems && value.length < prop.minItems) {
      return i18n.t('ui.minItems', 'Min {{count}} položek', { count: prop.minItems });
    }
    if (prop.maxItems && value.length > prop.maxItems) {
      return i18n.t('ui.maxItems', 'Max {{count}} položek', { count: prop.maxItems });
    }
  }
  return null;
}

// ── Field components ──────────────────────────────────────────────────────────

function FieldWrapper({ label, description, error, required, children }: {
  label?: string;
  description?: string;
  error?: string | null;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <div className='space-y-1'>
      {label && (
        <Label>
          {label}
          {required && <span className='text-red-400 ml-0.5'>*</span>}
        </Label>
      )}
      {children}
      {description && !error && <p className='text-[10px] text-gray-600'>{description}</p>}
      {error && <p className='text-[10px] text-red-400'>{error}</p>}
    </div>
  );
}

function StringField({ prop, value, onChange, readonly }: {
  prop: SchemaProperty;
  value: string;
  onChange: (v: string) => void;
  readonly?: boolean;
}) {
  // Enum → select
  if (prop.enum) {
    return (
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={readonly}
      >
        {prop.enum.map((opt) => (
          <option key={opt} value={opt} className='bg-gray-900'>{opt}</option>
        ))}
      </Select>
    );
  }

  // Textarea
  if (prop.format === 'textarea' || prop.format === 'code') {
    return (
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={prop.placeholder}
        disabled={readonly}
        className={prop.format === 'code' ? 'font-mono text-xs' : ''}
      />
    );
  }

  // Input
  const inputType = prop.format === 'password'
    ? 'password'
    : prop.format === 'email'
    ? 'email'
    : prop.format === 'url'
    ? 'url'
    : 'text';

  return (
    <Input
      type={inputType}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={prop.placeholder ?? prop.label}
      disabled={readonly}
    />
  );
}

function NumberField({ prop, value, onChange, readonly }: {
  prop: SchemaProperty;
  value: number;
  onChange: (v: number) => void;
  readonly?: boolean;
}) {
  return (
    <Input
      type='number'
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      min={prop.minimum}
      max={prop.maximum}
      step={prop.step ?? 1}
      disabled={readonly}
    />
  );
}

function ArrayField({ prop, value, onChange, readonly }: {
  prop: SchemaProperty;
  value: unknown[];
  onChange: (v: unknown[]) => void;
  readonly?: boolean;
}) {
  const itemSchema = prop.items ?? { type: 'string' as const };

  const addItem = () => {
    if (prop.maxItems && value.length >= prop.maxItems) return;
    onChange([...value, getDefault(itemSchema)]);
  };

  const removeItem = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, newVal: unknown) => {
    const next = [...value];
    next[index] = newVal;
    onChange(next);
  };

  return (
    <div className='space-y-2'>
      {value.map((item, i) => (
        <div key={i} className='flex items-start gap-2'>
          <div className='flex-1'>
            {itemSchema.type === 'string'
              ? (
                <Input
                  value={String(item ?? '')}
                  onChange={(e) => updateItem(i, e.target.value)}
                  disabled={readonly}
                  size='sm'
                />
              )
              : itemSchema.type === 'object' && itemSchema.properties
              ? (
                <ObjectField
                  prop={itemSchema}
                  value={(item as Record<string, unknown>) ?? {}}
                  onChange={(v) => updateItem(i, v)}
                  readonly={readonly}
                />
              )
              : (
                <Input
                  value={String(item ?? '')}
                  onChange={(e) => updateItem(i, e.target.value)}
                  disabled={readonly}
                  size='sm'
                />
              )}
          </div>
          {!readonly && (
            <button
              onClick={() => removeItem(i)}
              className='p-1.5 text-gray-600 hover:text-red-400 rounded-lg hover:bg-gray-800 mt-0.5'
            >
              <Trash2 size={12} />
            </button>
          )}
        </div>
      ))}
      {!readonly && (!prop.maxItems || value.length < prop.maxItems) && (
        <button
          onClick={addItem}
          className='flex items-center gap-1.5 text-xs text-plex hover:text-plex/80 py-1'
        >
          <Plus size={12} /> {i18n.t('ui.add', 'Přidat')}
        </button>
      )}
    </div>
  );
}

function ObjectField({ prop, value, onChange, readonly }: {
  prop: SchemaProperty;
  value: Record<string, unknown>;
  onChange: (v: Record<string, unknown>) => void;
  readonly?: boolean;
}) {
  const [collapsed, setCollapsed] = useState(false);

  if (!prop.properties) return null;

  return (
    <div className='border border-gray-800 rounded-xl overflow-hidden'>
      <button
        onClick={() => setCollapsed((c) => !c)}
        className='flex items-center gap-2 w-full px-3 py-2 text-xs font-medium text-gray-400 bg-gray-900/30 hover:bg-gray-800/30'
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
        {prop.label ?? 'Objekt'}
      </button>
      {!collapsed && (
        <div className='px-3 py-3 space-y-3'>
          {Object.entries(prop.properties).map(([key, subProp]) => (
            <SchemaField
              key={key}
              name={key}
              prop={subProp}
              value={value[key] ?? getDefault(subProp)}
              onChange={(v) => onChange({ ...value, [key]: v })}
              readonly={readonly}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Universal field renderer ──────────────────────────────────────────────────

function SchemaField({ name, prop, value, onChange, readonly }: {
  name: string;
  prop: SchemaProperty;
  value: unknown;
  onChange: (v: unknown) => void;
  readonly?: boolean;
}) {
  const error = validate(value, prop);
  const label = prop.label ?? name;

  if (prop.type === 'boolean') {
    return (
      <FieldWrapper label={label} description={prop.description} error={null}>
        <Toggle
          value={Boolean(value)}
          onSave={(v) => onChange(v)}
          disabled={readonly}
        />
      </FieldWrapper>
    );
  }

  if (prop.type === 'number') {
    return (
      <FieldWrapper
        label={label}
        description={prop.description}
        error={error}
        required={prop.required}
      >
        <NumberField
          prop={prop}
          value={Number(value ?? 0)}
          onChange={onChange}
          readonly={readonly}
        />
      </FieldWrapper>
    );
  }

  if (prop.type === 'array') {
    return (
      <FieldWrapper
        label={label}
        description={prop.description}
        error={error}
        required={prop.required}
      >
        <ArrayField
          prop={prop}
          value={Array.isArray(value) ? value : []}
          onChange={onChange}
          readonly={readonly}
        />
      </FieldWrapper>
    );
  }

  if (prop.type === 'object') {
    return (
      <ObjectField
        prop={{ ...prop, label }}
        value={(value as Record<string, unknown>) ?? {}}
        onChange={(v) => onChange(v)}
        readonly={readonly}
      />
    );
  }

  // Default: string
  return (
    <FieldWrapper
      label={label}
      description={prop.description}
      error={error}
      required={prop.required}
    >
      <StringField
        prop={prop}
        value={String(value ?? '')}
        onChange={onChange}
        readonly={readonly}
      />
    </FieldWrapper>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function SchemaForm(
  { schema, values, onChange, onSave, readonly, className, compact }: SchemaFormProps,
) {
  const { t } = useTranslation();
  const [localValues, setLocalValues] = useState<Record<string, unknown>>(values);
  const [saving, setSaving] = useState(false);

  const handleChange = useCallback((key: string, val: unknown) => {
    const next = { ...localValues, [key]: val };
    setLocalValues(next);
    onChange?.(next);
  }, [localValues, onChange]);

  const handleSave = async () => {
    if (!onSave || saving) return;
    setSaving(true);
    try {
      await onSave(localValues);
    } finally {
      setSaving(false);
    }
  };

  // Validace celého formuláře
  const hasErrors = Object.entries(schema).some(([key, prop]) =>
    prop.required && validate(localValues[key], prop) !== null
  );

  return (
    <div className={cn('space-y-4', compact && 'space-y-3', className)}>
      {Object.entries(schema).map(([key, prop]) => (
        <SchemaField
          key={key}
          name={key}
          prop={prop}
          value={localValues[key] ?? getDefault(prop)}
          onChange={(v) => handleChange(key, v)}
          readonly={readonly}
        />
      ))}

      {onSave && !readonly && (
        <div className='flex justify-end pt-2 border-t border-gray-800'>
          <button
            onClick={handleSave}
            disabled={saving || hasErrors}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors',
              hasErrors
                ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                : 'bg-plex hover:bg-plex/90 text-white',
            )}
          >
            {saving ? t('ui.saving', 'Ukládám…') : t('ui.save', 'Uložit')}
          </button>
        </div>
      )}
    </div>
  );
}
